import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Internal/portal routes we never redirect, even if a stray rule is configured.
const PROTECTED_PREFIXES = ["/portal/", "/seo-admin", "/api/", "/static/"];

function normalisePath(p) {
  if (!p) return "/";
  // Accept absolute URLs (https://domain.com/path) and extract pathname.
  let raw = String(p);
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try { raw = new URL(raw).pathname; } catch (_e) { /* keep raw */ }
  }
  let out = raw.split("?")[0].split("#")[0];
  if (out.length > 1 && out.endsWith("/")) out = out.slice(0, -1);
  return out;
}

/**
 * Lightweight client-side SEO redirect handler.
 *
 * - Fetches the active redirect table once per app mount from
 *   GET /api/seo-public/redirects (Speicher-configured rules with is_active=true).
 * - On every location change, checks whether the current pathname matches any
 *   active rule's `from_path` (whitespace-trimmed, trailing-slash-tolerant,
 *   case-insensitive).
 * - If matched, calls navigate(to_path + search + hash, { replace: true }).
 *   Using `replace: true` so the broken old URL is removed from history (mimics
 *   a 301 from the browser's history perspective) and the user can't accidentally
 *   bounce back to the dead slug via the back button.
 *
 * Limitations:
 * - This is a CLIENT-SIDE redirect. Crawlers that don't execute JS may still
 *   see the original URL as canonical. Long-term, you should also add server-
 *   level 301s via your edge/CDN config (Cloudflare Rules, Nginx, etc.) for
 *   maximum SEO juice. This hook is the immediate user-facing layer.
 */
export default function SEORedirectHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/seo-public/redirects`)
      .then((r) => {
        if (cancelled) return;
        const list = (r.data || [])
          .filter((rule) => rule && rule.from_path && rule.to_path && rule.is_active !== false)
          .map((rule) => ({
            from: normalisePath(rule.from_path).toLowerCase(),
            to: rule.to_path,
            statusCode: rule.status_code || 301,
          }))
          .filter((rule) => rule.from && rule.to && rule.from !== normalisePath(rule.to).toLowerCase());
        setRules(list);
      })
      .catch(() => setRules([]));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!rules.length) return;
    const here = normalisePath(location.pathname).toLowerCase();
    // Bail on protected routes — never let Speicher redirect away from /portal, /seo-admin, etc.
    if (PROTECTED_PREFIXES.some((pref) => location.pathname.startsWith(pref))) return;
    const hit = rules.find((rule) => rule.from === here);
    if (!hit) return;
    // Loop guard: if following the chain ever comes back here, don't redirect at all.
    const seen = new Set([here]);
    let next = normalisePath(hit.to).toLowerCase();
    for (let i = 0; i < 10; i += 1) {
      if (seen.has(next)) return;
      seen.add(next);
      const step = rules.find((rule) => rule.from === next);
      if (!step) break;
      next = normalisePath(step.to).toLowerCase();
    }
    // Resolve the destination: if it's a same-host absolute URL, navigate via SPA router;
    // if cross-origin, do a hard window.location redirect.
    const rawTo = String(hit.to || "");
    const isAbsolute = rawTo.startsWith("http://") || rawTo.startsWith("https://");
    if (isAbsolute) {
      try {
        const url = new URL(rawTo);
        if (url.origin === window.location.origin) {
          const dest = url.pathname + (location.search || "") + (location.hash || "");
          navigate(dest, { replace: true });
        } else {
          window.location.replace(rawTo + (location.search || "") + (location.hash || ""));
        }
      } catch (_e) {
        window.location.replace(rawTo);
      }
      return;
    }
    // Plain pathname — SPA navigate, preserving query + hash.
    const dest = rawTo + (location.search || "") + (location.hash || "");
    navigate(dest, { replace: true });
  }, [location.pathname, location.search, location.hash, rules, navigate]);

  return null;
}
